/**
 * ReviewSubmit Component
 * 
 * Final step in the AlertForm wizard that shows a summary of the alert configuration
 * and allows users to review before submitting.
 */

import React from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { 
  getAlertTypeDisplayText, 
  getSeverityDisplayText,
  getConditionDisplayText,
  AlertType
} from '../../../models/AlertModels';

/**
 * ReviewSubmit component for reviewing alert configuration before submission
 * 
 * @returns {JSX.Element} The ReviewSubmit component
 */
function ReviewSubmit() {
  const { formState } = useAlertContext();
  const { alert } = formState;
  
  /**
   * Format server scope for display
   * 
   * @returns {string} Formatted server scope
   */
  const formatServerScope = () => {
    if (alert.scope.type === 'global') {
      return 'All servers (Global)';
    } else if (alert.scope.serverIds && alert.scope.serverIds.length > 0) {
      return `${alert.scope.serverIds.length} specific server(s)`;
    } else {
      return 'No servers selected';
    }
  };
  
  /**
   * Format rules for display
   * 
   * @returns {JSX.Element} Formatted rules
   */
  const formatRules = () => {
    if (!alert.rules || alert.rules.length === 0) {
      return <div className="no-rules">No rules defined</div>;
    }
    
    // Helper function to check if a metric uses GB units
    const usesGBUnits = (eventName) => {
      return ['memory_usage', 'disk_usage', 'memory.available', 'disk.free'].includes(eventName);
    };
    
    return (
      <ul className="rules-summary-list">
        {alert.rules.map((rule, index) => (
          <li key={index} className="rule-summary-item">
            <span className="rule-event">{rule.event}</span>
            <span className="rule-condition">{getConditionDisplayText(rule.condition)}</span>
            <span className="rule-value">
              {rule.value}{usesGBUnits(rule.event) ? ' GB' : ''}
            </span>
            {/* Display process name targeting information if enabled */}
            {alert.type === AlertType.PROCESS && rule.processNameTargetingEnabled && (
              <div className="process-targeting-summary">
                <span className="process-targeting-label">Process targeting:</span>
                <span className="process-targeting-value">
                  {rule.processNamePattern || 'No pattern specified'}
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };
  
  /**
   * Format actions for display
   * 
   * @returns {JSX.Element} Formatted actions
   */
  const formatActions = () => {
    if (!alert.actions || alert.actions.length === 0) {
      return <div className="no-actions">No actions defined</div>;
    }
    
    return (
      <ul className="actions-summary-list">
        {alert.actions.map((action, index) => (
          <li key={index} className="action-summary-item">
            <i className="fa fa-check-circle"></i>
            <span>{action}</span>
          </li>
        ))}
      </ul>
    );
  };
  
  /**
   * Check if the alert configuration is valid
   * 
   * @returns {boolean} Whether the configuration is valid
   */
  const isAlertValid = () => {
    // Check required fields
    if (!alert.name || alert.name.trim() === '') return false;
    if (!alert.type) return false;
    if (!alert.severity) return false;
    
    // Check rules
    if (!alert.rules || alert.rules.length === 0) return false;
    
    // Check if all rules have values
    const invalidRules = alert.rules.filter(rule => 
      rule.value === undefined || rule.value === null || rule.value === ''
    );
    if (invalidRules.length > 0) return false;
    
    // Check process name targeting for Process alert type
    if (alert.type === AlertType.PROCESS) {
      const invalidProcessTargeting = alert.rules.filter(rule => 
        rule.processNameTargetingEnabled && (!rule.processNamePattern || rule.processNamePattern.trim() === '')
      );
      if (invalidProcessTargeting.length > 0) return false;
    }
    
    // Check server scope for server-specific alerts
    if (alert.scope.type === 'server-specific' && 
        (!alert.scope.serverIds || alert.scope.serverIds.length === 0)) {
      return false;
    }
    
    // Check actions
    if (!alert.actions || alert.actions.length === 0) return false;
    
    return true;
  };
  
  /**
   * Render validation warnings
   * 
   * @returns {JSX.Element|null} Validation warnings
   */
  const renderValidationWarnings = () => {
    const warnings = [];
    
    // Check required fields
    if (!alert.name || alert.name.trim() === '') {
      warnings.push('Alert name is required');
    }
    
    // Check rules
    if (!alert.rules || alert.rules.length === 0) {
      warnings.push('At least one rule is required');
    } else {
      // Check if all rules have values
      const invalidRules = alert.rules.filter(rule => 
        rule.value === undefined || rule.value === null || rule.value === ''
      );
      if (invalidRules.length > 0) {
        warnings.push('All rules must have values');
      }
      
      // Check process name targeting for Process alert type
      if (alert.type === AlertType.PROCESS) {
        const invalidProcessTargeting = alert.rules.filter(rule => 
          rule.processNameTargetingEnabled && (!rule.processNamePattern || rule.processNamePattern.trim() === '')
        );
        if (invalidProcessTargeting.length > 0) {
          warnings.push('Process Name/Pattern cannot be empty when targeting is enabled');
        }
      }
    }
    
    // Check server scope for server-specific alerts
    if (alert.scope.type === 'server-specific' && 
        (!alert.scope.serverIds || alert.scope.serverIds.length === 0)) {
      warnings.push('At least one server must be selected for server-specific alerts');
    }
    
    // Check actions
    if (!alert.actions || alert.actions.length === 0) {
      warnings.push('At least one action is required');
    }
    
    if (warnings.length === 0) return null;
    
    return (
      <div className="validation-warnings">
        <h4>Please fix the following issues:</h4>
        <ul>
          {warnings.map((warning, index) => (
            <li key={index}>{warning}</li>
          ))}
        </ul>
      </div>
    );
  };
  
  return (
    <div className="review-submit-container">
      <h3 className="step-title">Review & Submit</h3>
      <p className="step-description">
        Review your alert configuration before submitting. You can go back to previous steps to make changes if needed.
      </p>
      
      {renderValidationWarnings()}
      
      <div className="alert-summary">
        <div className="summary-section">
          <h4 className="summary-title">Basic Details</h4>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">Name:</span>
              <span className="summary-value">{alert.name || 'Not set'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Description:</span>
              <span className="summary-value">{alert.description || 'None'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Type:</span>
              <span className="summary-value">{getAlertTypeDisplayText(alert.type)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Severity:</span>
              <span className={`summary-value severity-${alert.severity?.toLowerCase()}`}>
                {getSeverityDisplayText(alert.severity) || 'Not set'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="summary-section">
          <h4 className="summary-title">Rules</h4>
          <div className="summary-content">
            {formatRules()}
          </div>
        </div>
        
        <div className="summary-section">
          <h4 className="summary-title">Server Scope</h4>
          <div className="summary-content">
            <div className="summary-item">
              <span className="summary-label">Applies to:</span>
              <span className="summary-value">{formatServerScope()}</span>
            </div>
          </div>
        </div>
        
        <div className="summary-section">
          <h4 className="summary-title">Actions</h4>
          <div className="summary-content">
            {formatActions()}
          </div>
        </div>
      </div>
      
      {!isAlertValid() && (
        <div className="submit-warning">
          <i className="fa fa-exclamation-triangle"></i>
          <p>
            Please fix the validation issues before submitting. You can go back to previous steps to make changes.
          </p>
        </div>
      )}
    </div>
  );
}

export default ReviewSubmit;
